import React, { useState } from 'react';
import {
  Box, Typography, Stack, Chip, Grid,
} from '@mui/material';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

// ── Drop optimised GIFs into public/images/clips/ and list them here ──────────
const CLIPS: {
  src: string;
  title: string;
  category: string;
  color: string;
}[] = [
  // { src: '/images/clips/badge-solder.gif', title: 'Soldering a conference badge', category: 'Hardware', color: '#ff9fd0' },
  // { src: '/images/clips/ctf-pwn.gif',      title: 'Pwning a heap challenge',       category: 'Hacking',  color: '#ff6eb4' },
  // { src: '/images/clips/blender-rig.gif',  title: 'Rigging the dragon avatar',     category: '3D',       color: '#b44fff' },
];


function GifCard({ src, title, category, color }: typeof CLIPS[0]) {
  const [hovered, setHovered] = useState(false);

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
        aspectRatio: '16/9',
        border: '1px solid rgba(255,110,180,0.12)',
        transition: 'border-color 0.25s, box-shadow 0.25s',
        ...(hovered && {
          borderColor: color + '88',
          boxShadow: `0 8px 32px ${color}22`,
        }),
      }}
    >
      <Box
        component="img"
        src={src}
        alt={title}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          transform: hovered ? 'scale(1.03)' : 'scale(1)',
          transition: 'transform 0.35s ease',
        }}
      />
      {/* Bottom label */}
      <Box sx={{
        position: 'absolute',
        bottom: 0, left: 0, right: 0,
        background: 'linear-gradient(0deg, rgba(13,10,18,0.92) 0%, transparent 100%)',
        px: 1.5, py: 1.2,
        opacity: hovered ? 1 : 0,
        transition: 'opacity 0.2s',
      }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={category}
            size="small"
            sx={{
              height: 18, fontSize: '0.62rem',
              background: `${color}18`,
              border: `1px solid ${color}44`,
              color,
            }}
          />
          <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.78rem' }}>
            {title}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}

export default function VideoSection() {
  return (
    <Box
      id="videos"
      sx={{
        py: { xs: 10, md: 14 },
        px: { xs: 3, md: 8 },
        background: 'linear-gradient(180deg, transparent, rgba(180,79,255,0.05), transparent)',
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 6, fontSize: '0.75rem' }}>
          02 — STREAMS
        </Typography>
        <Typography variant="h2" sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' }, mt: 1, mb: 2 }}>
          Watch Live
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 8, maxWidth: 480 }}>
          I stream hardware builds, CTF sessions, and 3D modelling live on Twitch.
        </Typography>

        {/* Twitch CTA */}
        <Box
          component="a"
          href="https://twitch.tv/panekopanko"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 3,
            p: { xs: 3, md: 4 },
            mb: CLIPS.length > 0 ? 8 : 0,
            borderRadius: 3,
            background: 'rgba(145,70,255,0.08)',
            border: '1px solid rgba(145,70,255,0.25)',
            textDecoration: 'none',
            transition: 'all 0.3s',
            '&:hover': {
              background: 'rgba(145,70,255,0.14)',
              borderColor: 'rgba(145,70,255,0.6)',
              boxShadow: '0 0 40px rgba(145,70,255,0.15)',
              '& .twitch-arrow': { transform: 'translate(3px, -3px)' },
            },
          }}
        >
          <Stack direction="row" spacing={2.5} alignItems="center">
            {/* Twitch purple circle with icon */}
            <Box sx={{
              width: 56, height: 56, borderRadius: '50%',
              background: 'rgba(145,70,255,0.18)',
              border: '1px solid rgba(145,70,255,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <LiveTvIcon sx={{ color: '#9146ff', fontSize: 28 }} />
            </Box>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.4 }}>
                <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700, lineHeight: 1 }}>
                  twitch.tv/panekopanko
                </Typography>
                <Chip
                  label="Tue / Wed / Sat"
                  size="small"
                  sx={{
                    height: 20, fontSize: '0.65rem',
                    background: 'rgba(145,70,255,0.15)',
                    border: '1px solid rgba(145,70,255,0.35)',
                    color: '#c084fc',
                  }}
                />
              </Stack>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Hardware builds · CTF · 3D modelling · general gremlin activity
              </Typography>
            </Box>
          </Stack>
          <OpenInNewIcon
            className="twitch-arrow"
            sx={{ color: '#9146ff', fontSize: 22, transition: 'transform 0.2s', flexShrink: 0 }}
          />
        </Box>

        {/* GIF showcase — only renders when CLIPS has entries */}
        {CLIPS.length > 0 && (
          <>
            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 3, fontSize: '0.7rem', display: 'block', mb: 3 }}>
              CLIPS & HIGHLIGHTS
            </Typography>
            <Grid container spacing={2.5}>
              {CLIPS.map((clip) => (
                <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={clip.src}>
                  <GifCard {...clip} />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Box>
    </Box>
  );
}
